import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navigation } from './Navigation';
import './Dashboard.css';
import './FivePillarsExplainer.css';

/* ── Data ── */

const PILLARS = [
  { num: 1, icon: '💰', name: 'Earning',    role: 'The Source',      principle: 'Maximize income deliberately',   bg: '#FEF3C7', text: '#92400e', lightText: '#fbbf24', border: '#fcd34d' },
  { num: 2, icon: '🏦', name: 'Saving',     role: 'The Foundation',  principle: 'Consistency over intensity',      bg: '#D1FAE5', text: '#276749', lightText: '#34d399', border: '#6ee7b7' },
  { num: 3, icon: '📈', name: 'Investing',  role: 'The Engine',      principle: 'Compounding over time',           bg: '#DBEAFE', text: '#1e40af', lightText: '#60a5fa', border: '#93c5fd' },
  { num: 4, icon: '⛓️', name: 'Debt',       role: 'The Anchor',      principle: 'Controlled, not zero',            bg: '#FEE2E2', text: '#991b1b', lightText: '#f87171', border: '#fca5a5' },
  { num: 5, icon: '🏝️', name: 'Retirement', role: 'The Destination', principle: 'Freedom Number = Expenses × 25', bg: '#E9D8FD', text: '#5b21b6', lightText: '#a78bfa', border: '#c4b5fd' },
] as const;

const SCORECARD_ITEMS = [
  { ...PILLARS[0], checks: ['Income growing at least 5%/year', 'Negotiated salary in last 2 years', 'Multiple income streams (or developing one)'] },
  { ...PILLARS[1], checks: ['Emergency fund: 3+ months', 'Savings rate: 20%+', 'Automated savings on payday'] },
  { ...PILLARS[2], checks: ['Contributing to 401k (at least to match)', 'Roth IRA funded', 'Low-cost index funds (<0.20% fees)'] },
  { ...PILLARS[3], checks: ['No credit card debt', 'No debt above 7% interest', 'Debt-to-income ratio under 36%'] },
  { ...PILLARS[4], checks: ['Know your Freedom Number', 'On track to hit it (or have a plan)', 'Portfolio over 25% of Freedom Number'] },
];

const STEPS = [
  { num: 1, label: 'What'  },
  { num: 2, label: 'Why'   },
  { num: 3, label: 'Which' },
  { num: 4, label: 'How'   },
  { num: 5, label: 'Ready' },
];

/* ── Utility ── */

function fmt(n: number) {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function calcYearsToFreedom(current: number, monthly: number, target: number): number {
  let portfolio = current;
  let years = 0;
  const monthlyReturn = 0.08 / 12;
  while (portfolio < target && years < 100) {
    for (let m = 0; m < 12; m++) {
      portfolio += monthly;
      portfolio *= (1 + monthlyReturn);
    }
    years++;
  }
  return years;
}

/* ── Step 1: What ── */

const StepWhat: React.FC = () => (
  <div>
    <p className="fpe-label">Step 1 of 5</p>
    <h2 className="fpe-title">The 5 Pillars of Wealth</h2>
    <p className="fpe-intro">
      Most people don't fail at building wealth because they lack intelligence.
      They fail because they never establish a <strong>system</strong>. Master each pillar in order, and wealth becomes inevitable.
    </p>

    <div className="fpe-pillars-grid">
      {PILLARS.map(p => (
        <div key={p.num} className="fpe-pillar-card" style={{ borderTopColor: p.border, backgroundColor: p.bg }}>
          <div className="fpe-pillar-icon">{p.icon}</div>
          <div className="fpe-pillar-tag" style={{ color: p.text }}>PILLAR {p.num}</div>
          <div className="fpe-pillar-name" style={{ color: p.text }}>{p.name}</div>
          <div className="fpe-pillar-role" style={{ color: p.text }}>{p.role}</div>
          <div className="fpe-pillar-principle">{p.principle}</div>
        </div>
      ))}
    </div>

    <div className="fpe-insight">
      <div className="fpe-insight-icon">💡</div>
      <p>
        <strong>The pillars work together.</strong> You can't invest what you don't save. You can't save what you don't earn.
        Debt drags everything down. And it all points toward one goal: making work <strong>optional</strong>.
      </p>
    </div>

    <div className="fpe-table-wrap">
      <table className="fpe-summary-table">
        <thead>
          <tr><th>Pillar</th><th>Name</th><th>Role</th><th>Core Principle</th></tr>
        </thead>
        <tbody>
          {PILLARS.map(p => (
            <tr key={p.num}>
              <td><span className="fpe-tbl-circle" style={{ backgroundColor: p.text }}>{p.num}</span></td>
              <td><span style={{ color: p.lightText, fontWeight: 700 }}>{p.name}</span></td>
              <td>{p.role}</td>
              <td>{p.principle}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

/* ── Step 2: Why ── */

const StepWhy: React.FC = () => (
  <div>
    <p className="fpe-label">Step 2 of 5</p>
    <h2 className="fpe-title">Why This Framework Works</h2>
    <p className="fpe-intro">
      Most financial advice is too vague, too scattered, or missing a priority order. The 5 Pillars fixes that.
    </p>

    <div className="fpe-compare-grid">
      <div className="fpe-compare-card fpe-compare-bad">
        <div className="fpe-compare-head"><span>✗</span> The Problem with Most Advice</div>
        <ul className="fpe-compare-list">
          <li>Hundreds of tips with no structure</li>
          <li>Too vague — "save more, spend less"</li>
          <li>No prioritization: what to do <strong>first?</strong></li>
          <li>Random tactics don't compound into wealth</li>
        </ul>
      </div>
      <div className="fpe-compare-card fpe-compare-good">
        <div className="fpe-compare-head"><span>✓</span> Why 5 Pillars Works</div>
        <ul className="fpe-compare-list">
          <li><strong>Complete:</strong> Covers every aspect of personal finance</li>
          <li><strong>Ordered:</strong> Clear priority when resources are limited</li>
          <li><strong>Connected:</strong> Each pillar strengthens the others</li>
          <li><strong>Measurable:</strong> Every pillar has metrics to track</li>
          <li><strong>Actionable:</strong> Specific steps, not vague advice</li>
        </ul>
      </div>
    </div>

    <div className="fpe-section-title">The Flywheel Effect</div>
    <div className="fpe-flywheel">
      {([
        { label: 'Earn More',    color: '#92400e', bg: '#FEF3C7', border: '#fcd34d' },
        { label: 'Save More',    color: '#276749', bg: '#D1FAE5', border: '#6ee7b7' },
        { label: 'Invest More',  color: '#1e40af', bg: '#DBEAFE', border: '#93c5fd' },
        { label: 'Grow Wealth',  color: '#5b21b6', bg: '#E9D8FD', border: '#c4b5fd' },
      ] as const).map((f, i, arr) => (
        <React.Fragment key={f.label}>
          <div className="fpe-flywheel-step" style={{ backgroundColor: f.bg, borderColor: f.border }}>
            <span style={{ color: f.color, fontWeight: 700, fontSize: '0.85rem' }}>{f.label}</span>
          </div>
          {i < arr.length - 1 && <span className="fpe-flywheel-arrow">→</span>}
        </React.Fragment>
      ))}
      <div className="fpe-flywheel-loop">↺ reinvest / compound</div>
    </div>

    <div className="fpe-why-cards">
      {([
        { detail: 'Every raise, side income, or skill upgrade adds fuel. Higher income means more to save and invest — Pillar 1 powers every other pillar.' },
        { detail: 'Increasing your savings rate by 1% every 6 months dramatically changes your timeline. Small, consistent gaps compound into significant wealth.' },
        { detail: 'Time in the market beats timing the market. The longer money compounds at 8%, the less manual work you need to add. Start early, stay invested.' },
        { detail: 'Eliminating high-interest debt frees cash flow for investing. Less drag on the flywheel means faster acceleration across all pillars.' },
        { detail: 'A clear Freedom Number keeps you motivated. Without a target, there\'s no finish line. Expenses × 25 gives you a specific, calculable goal.' },
      ] as const).map((item, i) => (
        <div key={i} className="fpe-why-card" style={{ borderLeftColor: PILLARS[i].border }}>
          <div className="fpe-why-header">
            <span className="fpe-why-icon">{PILLARS[i].icon}</span>
            <span className="fpe-why-name" style={{ color: PILLARS[i].lightText }}>
              Pillar {PILLARS[i].num} — {PILLARS[i].name}
            </span>
          </div>
          <p className="fpe-why-detail">{item.detail}</p>
        </div>
      ))}
    </div>
  </div>
);

/* ── Step 3: Which ── */

const SITUATIONS = [
  { situation: 'No emergency fund', pillarNum: 2, pillar: 'Saving', icon: '🏦',
    color: '#276749', bg: '#D1FAE5', border: '#6ee7b7',
    action: 'Build 3–6 months expenses in a HYSA', then: 'Start investing basics — 401k match only' },
  { situation: 'High-interest debt (>7%)', pillarNum: 4, pillar: 'Debt', icon: '⛓️',
    color: '#991b1b', bg: '#FEE2E2', border: '#fca5a5',
    action: 'Avalanche method — pay highest rate first', then: 'Hold other investing except 401k match' },
  { situation: 'Low income limiting everything', pillarNum: 1, pillar: 'Earning', icon: '💰',
    color: '#92400e', bg: '#FEF3C7', border: '#fcd34d',
    action: 'Negotiate salary, skill up, add side income', then: 'Apply every extra dollar to savings + investing' },
  { situation: 'Stable income but wealth not growing', pillarNum: 3, pillar: 'Investing', icon: '📈',
    color: '#1e40af', bg: '#DBEAFE', border: '#93c5fd',
    action: 'Max tax-advantaged accounts (Roth IRA, 401k)', then: 'Taxable brokerage with index funds after' },
  { situation: 'High income, still not wealthy', pillarNum: 2, pillar: 'Saving', icon: '🏦',
    color: '#276749', bg: '#D1FAE5', border: '#6ee7b7',
    action: 'Increase savings rate, close the lifestyle gap', then: 'Likely a spending problem, not an income problem' },
  { situation: 'On track and want to accelerate', pillarNum: 1, pillar: 'Earning + Investing', icon: '🚀',
    color: '#5b21b6', bg: '#E9D8FD', border: '#c4b5fd',
    action: 'Increase income AND investing simultaneously', then: 'Compound effect accelerates your timeline significantly' },
];

const StepWhich: React.FC = () => (
  <div>
    <p className="fpe-label">Step 3 of 5</p>
    <h2 className="fpe-title">Which Pillar Should You Focus On?</h2>
    <p className="fpe-intro">
      Resources are limited. Focus matters. Find your situation below to identify your highest-leverage move right now.
    </p>

    <div className="fpe-situation-grid">
      {SITUATIONS.map((s, i) => (
        <div key={i} className="fpe-situation-card" style={{ borderColor: s.border }}>
          <div className="fpe-situation-head">
            <span className="fpe-situation-icon">{s.icon}</span>
            <div className="fpe-situation-body">
              <div className="fpe-situation-title">{s.situation}</div>
              <div className="fpe-situation-focus" style={{ backgroundColor: s.bg }}>
                <span style={{ color: s.color, fontWeight: 700 }}>→ Pillar {s.pillarNum}: {s.pillar}</span>
              </div>
              <div className="fpe-situation-action"><strong>Action:</strong> {s.action}</div>
              <div className="fpe-situation-then"><strong>Then:</strong> {s.then}</div>
            </div>
          </div>
        </div>
      ))}
    </div>

    <div className="fpe-section-title" style={{ marginTop: '2.5rem' }}>General Priority Order</div>
    <div className="fpe-priority-list">
      {[
        { color: '#276749', bg: '#D1FAE5', text: 'Emergency fund — Pillar 2',          note: 'Always first. Without this, everything else is fragile.' },
        { color: '#991b1b', bg: '#FEE2E2', text: 'Eliminate toxic debt — Pillar 4',    note: 'Any debt above 7% APR destroys wealth faster than you can build it.' },
        { color: '#1e40af', bg: '#DBEAFE', text: '401k employer match — Pillar 3',     note: 'Free money. Always capture the full match before anything else.' },
        { color: '#92400e', bg: '#FEF3C7', text: 'Increase income — Pillar 1',         note: 'If you have a low ceiling, break through it. Negotiate. Skill up.' },
        { color: '#1e40af', bg: '#DBEAFE', text: 'Max tax-advantaged accounts — Pillar 3', note: 'Roth IRA ($7k/yr) + max 401k ($23k/yr). Tax-free growth compounds faster.' },
        { color: '#5b21b6', bg: '#E9D8FD', text: 'Calculate Freedom Number — Pillar 5', note: 'Know your target. Without a number, there is no destination.' },
        { color: '#1e40af', bg: '#DBEAFE', text: 'Taxable investing — Pillar 3',        note: 'After tax-advantaged accounts are maxed. VOO, VTI, SCHD.' },
      ].map((p, i) => (
        <div key={i} className="fpe-priority-row">
          <span className="fpe-priority-num" style={{ backgroundColor: p.bg, color: p.color }}>{i + 1}</span>
          <div className="fpe-priority-body">
            <div className="fpe-priority-text">{p.text}</div>
            <div className="fpe-priority-note">{p.note}</div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ── Step 4: How ── */

const PHASES = [
  {
    phase: 'Phase 1', title: 'Foundation', timeline: 'Months 1–6',
    color: '#34d399', bg: 'rgba(39,103,73,0.08)', border: 'rgba(110,231,183,0.25)',
    items: [
      { tag: 'P2', text: 'Calculate monthly expenses — core vs. lifestyle' },
      { tag: 'P2', text: 'Open a HYSA and automate savings on payday' },
      { tag: 'P2', text: 'Build to 1 month emergency fund' },
      { tag: 'P4', text: 'List all debts with balances and interest rates' },
      { tag: 'P4', text: 'Stop using credit cards if carrying a balance' },
      { tag: 'P4', text: 'Start avalanche method for any debt above 7%' },
      { tag: 'P3', text: 'Enroll in 401k up to full employer match' },
      { tag: 'P3', text: 'Open a Roth IRA (even if you start small)' },
    ],
  },
  {
    phase: 'Phase 2', title: 'Momentum', timeline: 'Months 6–24',
    color: '#60a5fa', bg: 'rgba(30,64,175,0.08)', border: 'rgba(147,197,253,0.25)',
    items: [
      { tag: 'P2', text: 'Build emergency fund to 3–6 months' },
      { tag: 'P2', text: 'Increase savings rate by 1% every 6 months' },
      { tag: 'P3', text: 'Max Roth IRA ($7,000/year)' },
      { tag: 'P3', text: 'Increase 401k contributions toward annual max' },
      { tag: 'P3', text: 'Choose low-cost index funds (VOO, VTI, SCHD)' },
      { tag: 'P1', text: 'Research salary benchmarks for your role' },
      { tag: 'P1', text: 'Have the compensation conversation' },
      { tag: 'P4', text: 'Eliminate all debt above 7% interest' },
    ],
  },
  {
    phase: 'Phase 3', title: 'Acceleration', timeline: 'Years 2–5',
    color: '#fbbf24', bg: 'rgba(146,64,14,0.08)', border: 'rgba(252,211,77,0.25)',
    items: [
      { tag: 'P1', text: 'Negotiate a raise or transition to a higher-paying role' },
      { tag: 'P1', text: 'Develop a secondary income stream' },
      { tag: 'P1', text: 'Invest in a high-ROI skill' },
      { tag: 'P3', text: 'Max all tax-advantaged accounts' },
      { tag: 'P3', text: 'Open a taxable brokerage after accounts are maxed' },
      { tag: 'P5', text: 'Calculate your Freedom Number (expenses × 25)' },
      { tag: 'P5', text: 'Project your timeline with FIRE Calculator' },
      { tag: 'P5', text: 'Model expense reduction to lower Freedom Number' },
    ],
  },
  {
    phase: 'Phase 4', title: 'Optimization', timeline: 'Years 5+',
    color: '#a78bfa', bg: 'rgba(91,33,182,0.08)', border: 'rgba(196,181,253,0.25)',
    items: [
      { tag: 'P3', text: 'Tax-loss harvesting in taxable accounts' },
      { tag: 'P3', text: 'Asset location optimization — what goes where' },
      { tag: 'P3', text: 'Annual rebalance, otherwise hands-off' },
      { tag: 'P5', text: 'Build 4-Bucket withdrawal system (10–15 yrs out)' },
      { tag: 'P5', text: 'Map your lifecycle allocation with the Lifecycle Planner' },
      { tag: 'P5', text: 'Consider Coast FI options' },
      { tag: 'P5', text: 'Plan the accumulation → distribution transition' },
    ],
  },
];

const StepHow: React.FC = () => {
  const [expenses, setExpenses] = useState('60000');
  const [portfolio, setPortfolio] = useState('150000');
  const [monthly,   setMonthly]   = useState('2000');

  const expNum  = parseFloat(expenses)  || 0;
  const portNum = parseFloat(portfolio) || 0;
  const monNum  = parseFloat(monthly)   || 0;

  const freedomNum = expNum * 25;
  const progress   = freedomNum > 0 ? Math.min(100, (portNum / freedomNum) * 100) : 0;
  const yearsLeft  = freedomNum > 0 && portNum < freedomNum && monNum > 0
    ? calcYearsToFreedom(portNum, monNum, freedomNum)
    : null;

  return (
    <div>
      <p className="fpe-label">Step 4 of 5</p>
      <h2 className="fpe-title">Your Implementation Roadmap</h2>
      <p className="fpe-intro">
        Four phases, each building on the last. Don't skip phases — they are ordered by impact.
      </p>

      <div className="fpe-phases">
        {PHASES.map(ph => (
          <div key={ph.phase} className="fpe-phase-card" style={{ backgroundColor: ph.bg, borderColor: ph.border }}>
            <div className="fpe-phase-header">
              <span className="fpe-phase-badge" style={{ color: ph.color }}>{ph.phase}</span>
              <span className="fpe-phase-title-text">{ph.title}</span>
              <span className="fpe-phase-timeline">{ph.timeline}</span>
            </div>
            <ul className="fpe-phase-list">
              {ph.items.map((item, i) => (
                <li key={i} className="fpe-phase-item">
                  <span className="fpe-phase-tag">{item.tag}</span>
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="fpe-calc-box">
        <div className="fpe-calc-title">Freedom Number Calculator</div>
        <div className="fpe-calc-sub">Your Freedom Number = Annual Expenses × 25. At that portfolio size, the 4% rule covers your expenses indefinitely.</div>
        <div className="fpe-calc-inputs fpe-calc-inputs-3">
          <label className="fpe-calc-label">
            Annual Expenses ($)
            <input type="number" className="fpe-calc-input" value={expenses} onChange={e => setExpenses(e.target.value)} placeholder="60000" />
          </label>
          <label className="fpe-calc-label">
            Current Portfolio ($)
            <input type="number" className="fpe-calc-input" value={portfolio} onChange={e => setPortfolio(e.target.value)} placeholder="150000" />
          </label>
          <label className="fpe-calc-label">
            Monthly Investment ($)
            <input type="number" className="fpe-calc-input" value={monthly} onChange={e => setMonthly(e.target.value)} placeholder="2000" />
          </label>
        </div>
        {expNum > 0 && (
          <div className="fpe-freedom-result">
            <div className="fpe-freedom-row">
              <span className="fpe-freedom-lbl">Your Freedom Number</span>
              <span className="fpe-freedom-val">{fmt(freedomNum)}</span>
            </div>
            <div className="fpe-freedom-row">
              <span className="fpe-freedom-lbl">Current Progress</span>
              <span className="fpe-freedom-val">{progress.toFixed(1)}%</span>
            </div>
            <div className="fpe-progress-bar-track">
              <div className="fpe-progress-bar-fill" style={{ width: `${progress}%` }} />
            </div>
            {portNum < freedomNum && yearsLeft !== null && (
              <div className="fpe-freedom-row">
                <span className="fpe-freedom-lbl">Years to Freedom Number (8% annual return)</span>
                <span className="fpe-freedom-val fpe-freedom-years">{yearsLeft} years</span>
              </div>
            )}
            {portNum >= freedomNum && (
              <div className="fpe-freedom-achieved">🎉 You've reached your Freedom Number!</div>
            )}
            <div className="fpe-freedom-note">
              Reducing expenses has <strong>double impact</strong>: lowers your Freedom Number AND frees more to invest now.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Step 5: Ready (Scorecard) ── */

interface StepReadyProps {
  scores: boolean[][];
  totalScore: number;
  toggleCheck: (pi: number, ci: number) => void;
  toolLink: (path: string) => string;
}

const StepReady: React.FC<StepReadyProps> = ({ scores, totalScore, toggleCheck, toolLink }) => {
  const maxScore = 15;

  const recommendation =
    totalScore >= 12 ? 'Strong foundation — focus on optimization and acceleration.'
    : totalScore >= 8  ? 'Good progress — identify and shore up your weakest pillars.'
    : totalScore >= 4  ? 'Building momentum — prioritize Phase 1 fundamentals.'
    : 'Start with Phase 1: emergency fund, 401k match, and eliminating toxic debt.';

  const weakestIdx = SCORECARD_ITEMS.reduce((minIdx, _, i) => {
    const cur = scores[i].filter(Boolean).length / SCORECARD_ITEMS[i].checks.length;
    const min = scores[minIdx].filter(Boolean).length / SCORECARD_ITEMS[minIdx].checks.length;
    return cur < min ? i : minIdx;
  }, 0);

  return (
    <div>
      <p className="fpe-label">Step 5 of 5</p>
      <h2 className="fpe-title">Your 5 Pillars Scorecard</h2>
      <p className="fpe-intro">
        Check every item that currently applies to you. This reveals your pillar health and where to focus next.
      </p>

      <div className="fpe-scorecard">
        {SCORECARD_ITEMS.map((item, pi) => {
          const pillarScore = scores[pi].filter(Boolean).length;
          return (
            <div key={pi} className="fpe-sc-pillar" style={{ borderColor: item.border }}>
              <div className="fpe-sc-pillar-head" style={{ backgroundColor: item.bg }}>
                <span className="fpe-sc-icon">{item.icon}</span>
                <span className="fpe-sc-name" style={{ color: item.text }}>
                  Pillar {item.num} — {item.name}
                </span>
                <span className="fpe-sc-score" style={{ color: item.text }}>
                  {pillarScore}/{item.checks.length}
                </span>
              </div>
              <div className="fpe-sc-checks">
                {item.checks.map((check, ci) => (
                  <div key={ci} className="fpe-sc-check" onClick={() => toggleCheck(pi, ci)}>
                    <span
                      className={`fpe-sc-box ${scores[pi][ci] ? 'checked' : ''}`}
                      style={scores[pi][ci] ? { backgroundColor: item.text, borderColor: item.text } : {}}
                    >
                      {scores[pi][ci] && '✓'}
                    </span>
                    <span className="fpe-sc-check-text">{check}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="fpe-score-result">
        <div className="fpe-score-display">
          <span className="fpe-score-num">{totalScore}</span>
          <span className="fpe-score-denom">/{maxScore}</span>
        </div>
        <div className="fpe-score-body">
          <div className="fpe-score-rec">{recommendation}</div>
          <div className="fpe-score-weak">
            Focus area:{' '}
            <strong style={{ color: SCORECARD_ITEMS[weakestIdx].lightText }}>
              Pillar {SCORECARD_ITEMS[weakestIdx].num} — {SCORECARD_ITEMS[weakestIdx].name}
            </strong>
          </div>
        </div>
      </div>

      <div className="fpe-cta-grid fpe-cta-grid-4">
        <div className="fpe-cta-card fpe-cta-card-primary">
          <div className="fpe-cta-icon">🔥</div>
          <h3>FIRE Calculator</h3>
          <p>Calculate your Freedom Number and exact years to financial independence.</p>
          <Link to={toolLink('/fire-calculator')} className="fpe-btn fpe-btn-primary">Launch Calculator →</Link>
        </div>
        <div className="fpe-cta-card">
          <div className="fpe-cta-icon">📅</div>
          <h3>Lifecycle Planner</h3>
          <p>Map how your investment allocation should shift across every stage of life.</p>
          <Link to={toolLink('/lifecycle-investment')} className="fpe-btn fpe-btn-outline">Open Planner →</Link>
        </div>
        <div className="fpe-cta-card">
          <div className="fpe-cta-icon">🪣</div>
          <h3>4-Bucket System</h3>
          <p>Structure your retirement withdrawals to survive any market crash.</p>
          <Link to="/learn/4-bucket-system" className="fpe-btn fpe-btn-outline">Read the Guide →</Link>
        </div>
        <div className="fpe-cta-card">
          <div className="fpe-cta-icon">📊</div>
          <h3>Wealth Dashboard</h3>
          <p>Track IV readings, market data, and your portfolio in one place.</p>
          <Link to={toolLink('/dashboard')} className="fpe-btn fpe-btn-outline">Go to Dashboard →</Link>
        </div>
      </div>

      <div className="fpe-cheatsheet">
        <div className="fpe-cheatsheet-title">The 5 Pillars Cheat Sheet</div>
        <div className="fpe-cheatsheet-rows">
          {[
            { lbl: 'P1 Earning',    tip: 'Maximize income deliberately',       act: 'Negotiate · Skill up · Multiple streams',                  ...PILLARS[0] },
            { lbl: 'P2 Saving',     tip: 'Consistency over intensity',          act: 'Automate · 20%+ rate · Emergency fund first',              ...PILLARS[1] },
            { lbl: 'P3 Investing',  tip: 'Time in market wins',                 act: 'Index funds · Max tax-advantaged · Don\'t touch',          ...PILLARS[2] },
            { lbl: 'P4 Debt',       tip: 'Controlled, not zero',                act: 'Eliminate >7% · Leverage <7% strategically',               ...PILLARS[3] },
            { lbl: 'P5 Retirement', tip: 'Freedom Number = Expenses × 25',      act: '4% rule · 4-Bucket system · Work optional',                ...PILLARS[4] },
          ].map(r => (
            <div key={r.lbl} className="fpe-cs-row" style={{ backgroundColor: r.bg }}>
              <span className="fpe-cs-label" style={{ color: r.text }}>{r.lbl}</span>
              <span className="fpe-cs-tip">{r.tip}</span>
              <span className="fpe-cs-arr">→</span>
              <span className="fpe-cs-act">{r.act}</span>
            </div>
          ))}
        </div>
        <div className="fpe-cheatsheet-priority">
          <strong>Priority order:</strong> Emergency Fund → Kill toxic debt → 401k match → Roth IRA → Max 401k → Taxable investing
        </div>
      </div>
    </div>
  );
};

/* ── Main Component ── */

export const FivePillarsExplainer: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);

  // Scorecard state lives here so it persists across step navigation
  const [scores, setScores] = useState<boolean[][]>(
    SCORECARD_ITEMS.map(s => s.checks.map(() => false))
  );

  const totalScore   = scores.flat().filter(Boolean).length;
  const toggleCheck  = (pi: number, ci: number) =>
    setScores(prev => prev.map((p, i) => i === pi ? p.map((c, j) => j === ci ? !c : c) : p));

  const toolLink = (path: string) => (isAuthenticated ? path : '/login');

  const goTo = (idx: number) => {
    setCurrentStep(idx);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const stepContent = [
    <StepWhat />,
    <StepWhy  />,
    <StepWhich />,
    <StepHow  />,
    <StepReady scores={scores} totalScore={totalScore} toggleCheck={toggleCheck} toolLink={toolLink} />,
  ];

  return (
    <div className={isAuthenticated ? 'dashboard-container' : ''}>
      {isAuthenticated && <Navigation />}
    <div className="fpe-page">

      {/* ── Nav (public only) ── */}
      {!isAuthenticated && (
      <nav className="fpe-nav">
        <Link to="/" className="fpe-nav-brand">
          <div className="fpe-nav-logo">W</div>
          <span className="fpe-nav-title">Wealth Financial Systems</span>
        </Link>
        <Link to="/login" className="fpe-nav-link">
          Sign In →
        </Link>
      </nav>
      )}

      {/* ── Hero ── */}
      <div className="fpe-hero">
        <div className="fpe-hero-badge">Wealth Building Framework</div>
        <h1>The 5 Pillars of Wealth</h1>
        <p className="fpe-hero-sub">
          A complete system for building financial freedom.<br />
          Master each pillar in order — wealth becomes inevitable.
        </p>
      </div>

      {/* ── Wizard ── */}
      <div className="fpe-wizard">

        {/* Step indicator */}
        <div className="fpe-steps-bar">
          {STEPS.map((s, i) => (
            <div key={s.num} className="fpe-step-item">
              <button
                className={`fpe-step-btn ${currentStep === i ? 'active' : ''} ${currentStep > i ? 'done' : ''}`}
                onClick={() => goTo(i)}
              >
                <span className="fpe-step-num">{s.num}</span>
                <span className="fpe-step-label">{s.label}</span>
              </button>
              {i < STEPS.length - 1 && <span className="fpe-step-arrow">›</span>}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div key={currentStep} className="fpe-step-content">
          {stepContent[currentStep]}
        </div>

        {/* Nav buttons */}
        <div className="fpe-wizard-nav">
          <span className="fpe-progress-text">{currentStep + 1} of {STEPS.length}</span>
          <div className="fpe-nav-btns">
            {currentStep > 0 && (
              <button className="fpe-btn fpe-btn-ghost" onClick={() => goTo(currentStep - 1)}>← Back</button>
            )}
            {currentStep < STEPS.length - 1 ? (
              <button className="fpe-btn fpe-btn-primary" onClick={() => goTo(currentStep + 1)}>Next →</button>
            ) : (
              <Link to={toolLink('/fire-calculator')} className="fpe-btn fpe-btn-primary">
                Launch FIRE Calculator →
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer className="fpe-footer">
        <p>
          Wealth Financial Systems · <Link to="/terms">Terms</Link> · <Link to="/privacy">Privacy</Link>
        </p>
        <p className="fpe-disclaimer">
          For educational purposes only. Not financial advice. Consult a qualified financial advisor before making investment decisions.
        </p>
      </footer>
    </div>
    </div>
  );
};
